import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LOGO = "/deck/assets/a-001-1c393ab857cb.png";

/**
 * Skye AI — Public intake form (/start)
 *
 * Dark hero welcome → 3-stage wizard (Start · Details · Last step) → thank you.
 * After the website field is filled, we ping /api/ads/suggest which scrapes
 * the site and asks Claude for ready-to-paste answers. Those show up as
 * clickable chips under the matching fields so owners finish 2× faster.
 */
export default function IntakeForm() {
  // ── Admin edit mode: /start?admin=<client_id> ─────────────────
  const adminClientId = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("admin") || "";
    } catch { return ""; }
  }, []);
  const adminMode = Boolean(adminClientId);

  // ── Form state ─────────────────────────────────────────────────
  const [state, setState] = useState(() => {
    if (adminClientId) return {};  // will hydrate from server
    try {
      const saved = sessionStorage.getItem("skye_intake_v3");
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      first_name: "", last_name: "", business_name: "",
      business_address: "", business_phone: "", business_email: "",
      website: "", project_lead: "",
      ai_greeting: "", company_details: "",
      qualifying_questions: "", faqs_objections: "",
      cant_claim: "",
      appointment_location: "",
      primary_contacts: "",
      has_additional_emails: "", additional_team_emails: "",
      has_sales_scripts: "", sales_scripts_notes: "", sales_scripts_url: "",
      sales_scripts_file: null,
      deadlines: "", ideal_customer: "", customer_platforms: "",
      customer_pain_points: "", social_urls: "", ad_service_area: "",
      current_lead_sources: "", usps: "", competitors: "",
      certifications: "",
      tagline: "", brand_colors: "", current_marketing_channels: [],
      has_existing_ad_accounts: "", monthly_budget: "",
      has_existing_assets: "", preferred_comm_channels: "",
      anything_else: "",
    };
  });
  // Admins start at step 1; public users start on the welcome screen.
  const [step, setStep] = useState(adminClientId ? 1 : 0);
  const [maxVisited, setMaxVisited] = useState(adminClientId ? 8 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [adminLoaded, setAdminLoaded] = useState(!adminClientId);
  const [error, setError] = useState("");

  // AI suggestions — keyed by field name. Empty until website analyzed.
  const [suggestions, setSuggestions] = useState({});
  const [suggestState, setSuggestState] = useState({ status: "idle", url: "" });
  const suggestTimer = useRef(null);
  const lastAnalyzedRef = useRef("");

  // ── Hydrate from server in admin mode ─────────────────────────
  useEffect(() => {
    if (!adminClientId) return;
    axios.get(`${API}/ads/clients/${adminClientId}`)
      .then((res) => {
        setState((s) => ({ ...s, ...res.data }));
        setAdminLoaded(true);
        // If we already had a website, kick suggestions.
        if (res.data?.website && !suggestions.company_details) {
          setTimeout(() => analyzeWebsite(res.data.website), 500);
        }
      })
      .catch(() => setError("Couldn't load this client — check the link."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminClientId]);

  useEffect(() => {
    if (adminMode) return;
    try { sessionStorage.setItem("skye_intake_v3", JSON.stringify(state)); } catch {}
  }, [state, adminMode]);

  const set = (k, v) => { setState((s) => ({ ...s, [k]: v })); setError(""); };

  // Kick a website analysis (debounced, single-run per URL)
  const analyzeWebsite = useCallback((raw) => {
    const url = (raw || "").trim();
    if (!url || url.length < 4 || url === lastAnalyzedRef.current) return;
    // Very loose validation — must look like a domain
    if (!/\.[a-z]{2,}/i.test(url) && !/^https?:\/\//i.test(url)) return;
    lastAnalyzedRef.current = url;
    setSuggestState({ status: "loading", url });
    axios.post(`${API}/ads/suggest`, { website: url })
      .then((res) => {
        if (res.data?.ok) {
          setSuggestions(res.data.suggestions || {});
          setSuggestState({ status: "ready", url });
        } else {
          setSuggestState({ status: "error", url, error: res.data?.error || "No suggestions available" });
        }
      })
      .catch(() => setSuggestState({ status: "error", url }));
  }, []);

  const scheduleAnalyze = (url) => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestTimer.current = setTimeout(() => analyzeWebsite(url), 700);
  };

  // 8 form sections grouped into 3 named stages, matching Fillout parity.
  const sections = useMemo(() => [
    { key: "about",   label: "Basics",           stage: "start"  },
    { key: "ai",      label: "The AI",           stage: "start"  },
    { key: "deep",    label: "Business details", stage: "start"  },
    { key: "comm",    label: "Communication",    stage: "details" },
    { key: "scripts", label: "Sales scripts",    stage: "details" },
    { key: "aud",     label: "Audience",         stage: "details" },
    { key: "pos",     label: "Positioning",      stage: "details" },
    { key: "brand",   label: "Brand & budget",   stage: "last"   },
  ], []);
  const total = sections.length;
  const isIntro = step === 0;
  const isLast = step === total;
  const currentSection = !isIntro ? sections[step - 1] : null;
  const activeStage = currentSection?.stage || "start";

  // Live completion — used for the thin progress bar + encouraging messages.
  // We count "meaningful" fields only (skip housekeeping toggles etc).
  const TRACKED_FIELDS = useMemo(() => [
    "first_name","last_name","business_name","business_address",
    "business_phone","business_email","website","project_lead",
    "company_details","qualifying_questions","faqs_objections",
    "appointment_location","primary_contacts","has_additional_emails",
    "has_sales_scripts","ideal_customer","ad_service_area","tagline",
    "brand_colors","current_marketing_channels","has_existing_ad_accounts",
    "monthly_budget","has_existing_assets",
  ], []);
  const completion = useMemo(() => {
    const filled = TRACKED_FIELDS.filter((k) => {
      const v = state[k];
      if (Array.isArray(v)) return v.length > 0;
      return typeof v === "string" ? v.trim().length > 0 : Boolean(v);
    }).length;
    return { filled, total: TRACKED_FIELDS.length, pct: Math.round((filled / TRACKED_FIELDS.length) * 100) };
  }, [state, TRACKED_FIELDS]);

  // ── Confetti micro-celebration on 50% and 100% crossings (once each). ──
  const milestonesRef = useRef({ fifty: false, hundred: false });
  useEffect(() => {
    if (adminMode) return;  // don't celebrate on the admin editor
    const p = completion.pct;
    if (p >= 50 && !milestonesRef.current.fifty) {
      milestonesRef.current.fifty = true;
      fireConfetti("half");
    }
    if (p >= 100 && !milestonesRef.current.hundred) {
      milestonesRef.current.hundred = true;
      fireConfetti("full");
    }
  }, [completion.pct, adminMode]);

  const stepCheer = useMemo(() => {
    const p = completion.pct;
    if (p >= 90) return "Almost there — nailing it.";
    if (p >= 70) return "Home stretch. You're doing great.";
    if (p >= 45) return "Halfway there — keep the momentum.";
    if (p >= 20) return "Rolling now — nice pace.";
    return "You've got this — quick to fill.";
  }, [completion.pct]);

  // ── Per-step validation ───────────────────────────────────────
  const validate = () => {
    const s = state;
    const missing = (k) => !String(s[k] || "").trim();
    if (step === 1) {
      for (const k of ["first_name","last_name","business_name","business_address",
                       "business_phone","business_email","website","project_lead"]) {
        if (missing(k)) return `Please fill in ${labelFor(k)}.`;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.business_email)) return "That email looks off.";
      if (!/^https?:\/\/|^www\.|\.[a-z]{2,}/i.test(s.website)) return "Website URL looks off.";
    }
    if (step === 2 && missing("company_details")) return "Company details are required.";
    if (step === 3) {
      for (const k of ["qualifying_questions","faqs_objections","appointment_location"]) {
        if (missing(k)) return `Please answer: ${labelFor(k)}.`;
      }
    }
    if (step === 4) {
      if (missing("primary_contacts")) return "Please share your primary contact channels.";
      if (s.has_additional_emails === "YES" && missing("additional_team_emails"))
        return "Please add the email addresses to invite.";
    }
    if (step === 5 && s.has_sales_scripts === "YES") {
      if (missing("sales_scripts_url") && missing("sales_scripts_notes") && !s.sales_scripts_file)
        return "Paste a Google Doc URL, notes, or upload your script PDF.";
    }
    if (step === 6 && (missing("ideal_customer") || missing("ad_service_area")))
      return "Ideal customer + ad service area are both required.";
    if (step === 8 && missing("monthly_budget"))
      return "Please pick a monthly ad budget.";
    return "";
  };

  // ── Persistence helpers ────────────────────────────────────────
  const saveToServer = useCallback(async (partial) => {
    if (!adminClientId) return;
    try {
      await axios.patch(`${API}/ads/clients/${adminClientId}`, { updates: partial });
    } catch { /* silent — the top-level save button retries */ }
  }, [adminClientId]);

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (step < total) {
      const nextStep = step + 1;
      setStep(nextStep);
      if (nextStep > maxVisited) setMaxVisited(nextStep);
      if (adminMode) saveToServer(state);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submit();
    }
  };
  const back = () => {
    if (step > 0) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const jumpTo = (target) => {
    // Free navigation backward, or forward only up to maxVisited (admins uncapped)
    if (target === step) return;
    if (target > maxVisited) return;
    if (adminMode) saveToServer(state);
    setStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      if (adminMode) {
        // Save final step then bounce back to the admin
        await axios.patch(`${API}/ads/clients/${adminClientId}`, { updates: state });
        setSubmitting(false);
        window.location.href = `/?admin=1#client-${adminClientId}`;
        return;
      }
      const payload = { ...state };
      if (!payload.sales_scripts_file) payload.sales_scripts_file = {};
      if (payload.website && !/^https?:\/\//i.test(payload.website))
        payload.website = "https://" + payload.website.trim();
      const res = await axios.post(`${API}/ads/intake`, payload);
      if (res.data?.success) {
        try { sessionStorage.removeItem("skye_intake_v3"); } catch {}
        setSubmitted(true);
      } else {
        setError("Something went sideways. Please try again.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const first = detail[0]?.msg || "Validation error";
        setError(String(first).slice(0, 200));
      } else {
        setError(String(detail || e?.message || "Network error"));
      }
    }
    setSubmitting(false);
  };

  if (submitted) return <ThankYou />;
  if (adminMode && !adminLoaded) return (
    <div style={sh.page}>
      <BgFx />
      <div style={sh.welcomeWrap}>
        <div style={{ ...sh.welcomeInner, maxWidth: 420 }}>
          <div style={sh.doneChip}>Loading client…</div>
        </div>
      </div>
    </div>
  );

  const stepProps = { state, set, suggestions, suggestState };

  // ═════════════════════════════════════════════════════════════════
  return (
    <div style={sh.page} data-testid="intake-form">
      <BgFx />

      {isIntro ? (
        <Welcome onStart={() => { setStep(1); setMaxVisited(1); }} />
      ) : (
        <>
          {adminMode && (
            <div style={sh.adminBanner} data-testid="intake-admin-banner">
              <span style={sh.adminBadge}>ADMIN EDIT MODE</span>
              <span style={sh.adminMeta}>
                Editing {state.business_name || "client"} · changes save when you move between steps
              </span>
              <a href={`/?admin=1#client-${adminClientId}`} style={sh.adminBack} data-testid="intake-admin-back">
                ← Back to Ads Hub
              </a>
            </div>
          )}
          <StageProgress activeStage={activeStage} step={step} total={total} sectionLabel={currentSection.label} />
          <CompletionBar filled={completion.filled} total={completion.total} pct={completion.pct} cheer={stepCheer} />
          {adminMode ? (
            <StepsNavigator
              sections={sections}
              step={step}
              maxVisited={maxVisited}
              adminMode={adminMode}
              onJump={jumpTo}
            />
          ) : (
            <SectionsDropdown
              sections={sections}
              step={step}
              maxVisited={maxVisited}
              onJump={jumpTo}
            />
          )}
          <div style={sh.content}>
            <div style={sh.cardWrap} key={step}>
              <GradientCard>
                {step === 1 && <StepAbout {...stepProps} scheduleAnalyze={scheduleAnalyze} />}
                {step === 2 && <StepAI {...stepProps} />}
                {step === 3 && <StepDeep {...stepProps} />}
                {step === 4 && <StepComm {...stepProps} />}
                {step === 5 && <StepScripts {...stepProps} />}
                {step === 6 && <StepAudience {...stepProps} />}
                {step === 7 && <StepPositioning {...stepProps} />}
                {step === 8 && <StepBrand {...stepProps} />}

                <div style={sh.actions}>
                  {error && <div style={sh.error} data-testid="intake-error">{error}</div>}
                  <div style={sh.actionRow}>
                    {step > 1 ? (
                      <button onClick={back} data-testid="intake-back" style={sh.btnGhost}>← Back</button>
                    ) : <span />}
                    <button
                      onClick={next}
                      disabled={submitting}
                      data-testid="intake-next"
                      style={{ ...sh.btnPrimary, opacity: submitting ? 0.6 : 1 }}
                    >
                      {submitting ? "Saving…" : isLast ? (adminMode ? "Save & close" : "Submit form →") : "Continue →"}
                    </button>
                  </div>
                </div>
              </GradientCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// WELCOME
// ═════════════════════════════════════════════════════════════════════
function Welcome({ onStart }) {
  return (
    <div style={sh.welcomeWrap} data-testid="intake-welcome">
      <div style={sh.welcomeInner}>
        <img src={LOGO} alt="Skye AI" style={sh.welcomeLogo} data-testid="intake-welcome-logo" />
        <h1 style={sh.welcomeTitle}>Welcome! Onboarding Form</h1>
        <p style={sh.welcomeLead}>
          Please complete this survey so that we can better understand your
          business &amp; goals to prepare for the Kickoff Call.
        </p>
        <button onClick={onStart} data-testid="intake-start" style={sh.welcomeBtn}>
          Click Here To Start!
        </button>
        <div style={sh.welcomeFoot}>
          Autosaves as you go
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STAGE PROGRESS
// ═════════════════════════════════════════════════════════════════════
function StageProgress({ activeStage, step, total, sectionLabel }) {
  const stages = [
    { key: "start", label: "Start" },
    { key: "details", label: "Details" },
    { key: "last", label: "Last step" },
  ];
  const activeIdx = stages.findIndex((s) => s.key === activeStage);
  return (
    <div style={sh.stageWrap} data-testid="intake-progress">
      <div style={sh.stageRow}>
        {stages.map((s, i) => {
          const isActive = i === activeIdx;
          const isDone = i < activeIdx;
          return (
            <div key={s.key} style={sh.stageItem}>
              <div style={{
                ...sh.stageDot,
                background: isActive
                  ? "linear-gradient(135deg, #4ADEDE, #B476FF)"
                  : isDone ? "#4ADEDE" : "rgba(255,255,255,0.08)",
                borderColor: isActive ? "#4ADEDE" : isDone ? "#4ADEDE" : "rgba(255,255,255,0.14)",
                boxShadow: isActive ? "0 0 20px rgba(74,222,222,0.5)" : "none",
              }}>
                {isDone && <span style={{ color: "#0A0A0A", fontSize: 11, fontWeight: 900 }}>✓</span>}
              </div>
              <span style={{
                ...sh.stageLabel,
                color: isActive ? "#fff" : isDone ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)",
                fontWeight: isActive ? 700 : 600,
              }}>{s.label}</span>
              {i < stages.length - 1 && (
                <div style={{
                  ...sh.stageLine,
                  background: isDone
                    ? "linear-gradient(90deg, #4ADEDE, rgba(180,118,255,0.6))"
                    : "rgba(255,255,255,0.08)",
                }} />
              )}
            </div>
          );
        })}
      </div>
      <div style={sh.stageMeta}>Step {step} of {total} · {sectionLabel}</div>
    </div>
  );
}

function GradientCard({ children }) {
  return (
    <div style={sh.gradBorder}>
      <div style={sh.gradInner}>{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// COMPLETION BAR — thin gradient bar + encouraging one-liner. Not a
// timer; just live feedback so people feel progress every keystroke.
// ═════════════════════════════════════════════════════════════════════
function CompletionBar({ filled, total, pct, cheer }) {
  return (
    <div style={sh.completionWrap} data-testid="intake-completion-bar">
      <div style={sh.completionRow}>
        <span style={sh.completionCheer}>{cheer}</span>
        <span style={sh.completionCount} data-testid="intake-completion-pct">
          {filled} / {total} · {pct}%
        </span>
      </div>
      <div style={sh.completionTrack}>
        <div style={{ ...sh.completionFill, width: `${Math.max(4, pct)}%` }} />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTIONS DROPDOWN (public users)
// Replaces the "8 pills" strip so the form doesn't look overwhelming.
// User can still jump backwards to any completed section.
// ═════════════════════════════════════════════════════════════════════
function SectionsDropdown({ sections, step, maxVisited, onJump }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={wrap} style={sh.secDropWrap} data-testid="intake-sections-dropdown">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="intake-sections-toggle"
        style={sh.secDropBtn}
      >
        Sections ▾
      </button>
      {open && (
        <div style={sh.secDropList} data-testid="intake-sections-list">
          {sections.map((s, i) => {
            const idx = i + 1;
            const isActive = idx === step;
            const isVisited = idx <= maxVisited;
            const canJump = isVisited;
            return (
              <button
                key={s.key}
                type="button"
                disabled={!canJump}
                onClick={() => { if (canJump) { onJump(idx); setOpen(false); } }}
                data-testid={`intake-section-item-${idx}`}
                style={{
                  ...sh.secDropItem,
                  opacity: canJump ? 1 : 0.4,
                  background: isActive ? "rgba(180,118,255,0.14)" : "transparent",
                  color: isActive ? "#fff" : canJump ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.4)",
                }}
              >
                <span style={{
                  ...sh.secDropNum,
                  background: isActive
                    ? "linear-gradient(135deg, #4ADEDE, #B476FF)"
                    : idx < step ? "#4ADEDE" : "transparent",
                  color: (isActive || idx < step) ? "#0A0A0A" : "rgba(255,255,255,0.5)",
                  border: (isActive || idx < step) ? "none" : "1px solid rgba(255,255,255,0.14)",
                }}>
                  {idx < step ? "✓" : idx}
                </span>
                {s.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEPS NAVIGATOR — 8 numbered dots the user can click to jump around.
// Backwards navigation is always free; forward requires the step has been
// visited (public users) or admin mode.
// ═════════════════════════════════════════════════════════════════════
function StepsNavigator({ sections, step, maxVisited, adminMode, onJump }) {
  return (
    <div style={sh.stepsNav} data-testid="intake-steps-nav">
      {sections.map((s, i) => {
        const idx = i + 1;
        const isActive = idx === step;
        const isDone = idx < step;
        const canJump = adminMode || idx <= maxVisited;
        return (
          <button
            key={s.key}
            type="button"
            disabled={!canJump}
            onClick={() => canJump && onJump(idx)}
            data-testid={`intake-step-nav-${idx}`}
            style={{
              ...sh.stepsNavItem,
              cursor: canJump ? "pointer" : "not-allowed",
              opacity: canJump ? 1 : 0.35,
              background: isActive
                ? "linear-gradient(135deg, rgba(74,222,222,0.15), rgba(180,118,255,0.15))"
                : isDone ? "rgba(74,222,222,0.06)" : "rgba(255,255,255,0.02)",
              borderColor: isActive
                ? "rgba(180,118,255,0.55)"
                : isDone ? "rgba(74,222,222,0.35)" : "rgba(255,255,255,0.06)",
              color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
            }}
          >
            <span style={{
              ...sh.stepsNavNum,
              background: isActive
                ? "linear-gradient(135deg, #4ADEDE, #B476FF)"
                : isDone ? "#4ADEDE" : "transparent",
              color: isActive || isDone ? "#0A0A0A" : "rgba(255,255,255,0.5)",
              border: isDone || isActive ? "none" : "1px solid rgba(255,255,255,0.14)",
            }}>{isDone ? "✓" : idx}</span>
            <span style={sh.stepsNavLabel}>{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STEPS
// ═════════════════════════════════════════════════════════════════════
function StepAbout({ state, set, scheduleAnalyze, suggestState }) {
  return (
    <StepShell title="Lets Get The Basics Down!" lead="First, we'd love to know a bit about you. Drop your website and we'll read it to pre-fill answers throughout the form.">
      <Row>
        <Text label="Your First Name *" tid="first_name" value={state.first_name} onChange={(v) => set("first_name", v)} />
        <Text label="Your Last Name *" tid="last_name" value={state.last_name} onChange={(v) => set("last_name", v)} />
      </Row>
      <Text label="Your Business/Organization's Legal Name *" tid="business_name" value={state.business_name} onChange={(v) => set("business_name", v)} />
      <AddressAutocomplete
        label="Business Address *"
        tid="business_address"
        value={state.business_address}
        onChange={(v) => set("business_address", v)}
      />
      <Row>
        <Text label="Business Phone Number *" tid="business_phone" value={state.business_phone} onChange={(v) => set("business_phone", v)} type="tel" />
        <Text label="Business Email *" tid="business_email" value={state.business_email} onChange={(v) => set("business_email", v)} type="email" />
      </Row>
      <WebsiteField
        value={state.website}
        onChange={(v) => { set("website", v); scheduleAnalyze(v); }}
        onBlur={() => scheduleAnalyze(state.website)}
        suggestState={suggestState}
      />
      <Text label="Team Member(s) Leading This Project *" tid="project_lead" value={state.project_lead} onChange={(v) => set("project_lead", v)} placeholder="First & last name" />
    </StepShell>
  );
}

function StepAI({ state, set, suggestions }) {
  return (
    <StepShell title="Now Let's Set Up The AI" lead="What your AI setter needs in its brain to sound like you on every call.">
      <LongText
        label="What should the AI say after the greeting? (optional)"
        tid="ai_greeting" value={state.ai_greeting}
        onChange={(v) => set("ai_greeting", v)}
        hint="Skip this if you want us to write it. Otherwise, give the exact intro after 'Hello.'"
      />
      <LongText
        label="Company Details *"
        tid="company_details"
        value={state.company_details}
        onChange={(v) => set("company_details", v)}
        hint="Everything the bot should know to pitch your business: specialty, offers, discounts, packages, pricing. Call-ready — nothing that shouldn't be said out loud."
        suggestion={suggestions.company_details}
        onApplySuggestion={(v) => set("company_details", v)}
      />
    </StepShell>
  );
}

function StepDeep({ state, set, suggestions }) {
  return (
    <StepShell title="Deeper Business Details" lead="Qualifying, objections, claims to avoid, and where the appointment lands.">
      <LongText
        label="Qualifying Questions (if any) *"
        tid="qualifying_questions"
        value={state.qualifying_questions}
        onChange={(v) => set("qualifying_questions", v)}
        hint="Questions your AI should ask to qualify or gather info. Ex: 1) Are they a homeowner in Nottingham, PA or nearby? 2) Any upcoming asphalt/concrete projects?"
        suggestion={suggestions.qualifying_questions}
        onApplySuggestion={(v) => set("qualifying_questions", v)}
      />
      <LongText
        label="FAQs · Objection Handling *"
        tid="faqs_objections"
        value={state.faqs_objections}
        onChange={(v) => set("faqs_objections", v)}
        hint={`Common questions & objections with how the bot should respond. Ex: "Why should I trust your business?" → "We're family-owned, 20 years of experience…"`}
        suggestion={suggestions.faqs_objections}
        onApplySuggestion={(v) => set("faqs_objections", v)}
      />
      <LongText
        label="Anything you legally can't say or don't want claimed?"
        tid="cant_claim"
        value={state.cant_claim}
        onChange={(v) => set("cant_claim", v)}
        hint={`Keeps our ads compliant and on-brand. Ex: "Can't say 'guaranteed cure' — regulated industry", "Don't mention specific competitors by name", "Avoid claims about specific outcomes without a disclaimer."`}
        suggestion={suggestions.cant_claim}
        onApplySuggestion={(v) => set("cant_claim", v)}
      />
      <Radio
        label="Where Does The Appointment Land? *"
        tid="appointment_location"
        options={["Zoom/Video call", "In-Person", "Phone Call"]}
        value={state.appointment_location}
        onChange={(v) => set("appointment_location", v)}
      />
    </StepShell>
  );
}

function StepComm({ state, set }) {
  return (
    <StepShell title="How Do We Reach You?" lead="We run daily communication through Slack — quick DMs during the build, no group chats you didn't ask for.">
      <Text
        label="Primary Phone + Email For Urgent Needs *"
        tid="primary_contacts"
        value={state.primary_contacts}
        onChange={(v) => set("primary_contacts", v)}
        placeholder="(123) 456-7890 · you@domain.com"
      />
      <Radio
        label="Have additional emails of important team members to invite to the Slack group chat? *"
        tid="has_additional_emails"
        options={["YES", "NO"]}
        value={state.has_additional_emails}
        onChange={(v) => set("has_additional_emails", v)}
      />
      {state.has_additional_emails === "YES" && (
        <LongText
          label="Input Emails Below *"
          tid="additional_team_emails"
          value={state.additional_team_emails}
          onChange={(v) => set("additional_team_emails", v)}
          hint="Names + emails, one per line — we'll send Slack invites."
        />
      )}
    </StepShell>
  );
}

function StepScripts({ state, set }) {
  return (
    <StepShell title="Any Sales Scripts?" lead="If you have proven scripts, share them — we train the AI on your voice.">
      <Radio
        label="Do you have sales scripts you'd like the AI to learn from? *"
        tid="has_sales_scripts"
        options={["YES", "NO"]}
        value={state.has_sales_scripts}
        onChange={(v) => set("has_sales_scripts", v)}
      />
      {state.has_sales_scripts === "YES" && (
        <>
          <Text
            label="Google Doc URL"
            tid="sales_scripts_url"
            value={state.sales_scripts_url}
            onChange={(v) => set("sales_scripts_url", v)}
            placeholder="https://docs.google.com/document/…"
          />
          <PdfUpload
            label="…or drop a PDF (max 20 MB)"
            tid="sales_scripts_file"
            file={state.sales_scripts_file}
            onChange={(f) => set("sales_scripts_file", f)}
          />
          <LongText
            label="Any Additional Talking Points, Closing Lines, Or Notes? (optional)"
            tid="sales_scripts_notes"
            value={state.sales_scripts_notes}
            onChange={(v) => set("sales_scripts_notes", v)}
          />
        </>
      )}
    </StepShell>
  );
}

function StepAudience({ state, set, suggestions }) {
  return (
    <StepShell title="Your Ideal Audience" lead="The people you want walking through the door.">
      <Text label="Any Milestones Or Deadlines? (optional)" tid="deadlines" value={state.deadlines} onChange={(v) => set("deadlines", v)} />
      <Text
        label="Who Is Your Ideal Customer? *"
        tid="ideal_customer"
        value={state.ideal_customer}
        onChange={(v) => set("ideal_customer", v)}
        placeholder="Age, gender, location, income, homeowner, job title…"
        suggestion={suggestions.ideal_customer}
        onApplySuggestion={(v) => set("ideal_customer", v)}
      />
      <Text label="Where Do Your Customers Hang Out Online?" tid="customer_platforms" value={state.customer_platforms} onChange={(v) => set("customer_platforms", v)} placeholder="Facebook, Instagram, LinkedIn, YouTube…" />
      <Text
        label="Their Main Pain Points?"
        tid="customer_pain_points"
        value={state.customer_pain_points}
        onChange={(v) => set("customer_pain_points", v)}
        suggestion={suggestions.customer_pain_points}
        onApplySuggestion={(v) => set("customer_pain_points", v)}
      />
      <Text label="Your Facebook / Instagram URL" tid="social_urls" value={state.social_urls} onChange={(v) => set("social_urls", v)} />
      <Text
        label="Where Should We Advertise? *"
        tid="ad_service_area"
        value={state.ad_service_area}
        onChange={(v) => set("ad_service_area", v)}
        placeholder="City + mile radius or list of ZIP codes — e.g. Clermont, Florida (30 mi)"
        suggestion={suggestions.ad_service_area}
        onApplySuggestion={(v) => set("ad_service_area", v)}
      />
    </StepShell>
  );
}

function StepPositioning({ state, set, suggestions }) {
  const uspsOptions = Array.isArray(suggestions.usps_options) ? suggestions.usps_options : [];
  const uspsEmpty = !String(state.usps || "").trim();
  return (
    <StepShell title="What Makes You Different" lead="Everything we lean on to make your ads win.">
      <Text label="How Do You Currently Attract Customers?" tid="current_lead_sources" value={state.current_lead_sources} onChange={(v) => set("current_lead_sources", v)} placeholder="Referrals, promotions, paid ads, etc." />
      <Text
        label="Your USPs (Unique Selling Points)?"
        tid="usps"
        value={state.usps}
        onChange={(v) => set("usps", v)}
        suggestion={suggestions.usps}
        onApplySuggestion={(v) => set("usps", v)}
      />
      {uspsEmpty && uspsOptions.length > 0 && (
        <div style={sh.uspsAlt} data-testid="intake-usps-alternatives">
          <div style={sh.uspsAltHead}>
            <span style={sh.suggestBadge}>SUGGEST</span>
            <span style={sh.uspsAltTitle}>Not sure? Try one of these — you can edit after</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {uspsOptions.slice(0, 3).map((opt, i) => (
              <button
                key={i}
                type="button"
                onClick={() => set("usps", opt)}
                data-testid={`intake-usps-alt-${i}`}
                style={sh.uspsAltBtn}
              >
                <span style={{ flex: 1, textAlign: "left", fontSize: 13, lineHeight: 1.5, color: "rgba(255,255,255,0.9)" }}>{opt}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#4ADEDE" }}>Use →</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <Text
        label="Main Competitors?"
        tid="competitors"
        value={state.competitors}
        onChange={(v) => set("competitors", v)}
        placeholder="List their names"
        suggestion={suggestions.competitors}
        onApplySuggestion={(v) => set("competitors", v)}
      />
      <Text
        label="Certifications We Can Mention For Trust?"
        tid="certifications"
        value={state.certifications}
        onChange={(v) => set("certifications", v)}
        suggestion={suggestions.certifications}
        onApplySuggestion={(v) => set("certifications", v)}
      />
    </StepShell>
  );
}

function StepBrand({ state, set, suggestions }) {
  return (
    <StepShell title="Brand · Budget · Logistics" lead="Last leg — how the ads look and how much you're spending.">
      <Text
        label="Your Current Tagline Or Slogan?"
        tid="tagline"
        value={state.tagline}
        onChange={(v) => set("tagline", v)}
        suggestion={suggestions.tagline}
        onApplySuggestion={(v) => set("tagline", v)}
      />
      <Text
        label="Your Brand Colors?"
        tid="brand_colors"
        value={state.brand_colors}
        onChange={(v) => set("brand_colors", v)}
        placeholder="e.g. #0F2A44, #C9A34E or 'dark green and yellow'"
        hint="Hex codes preferred — we auto-scrape these from your website if we can."
        suggestion={suggestions.brand_colors}
        onApplySuggestion={(v) => set("brand_colors", v)}
      />
      <Checkboxes
        label="What Marketing Are You Currently Running? (select all)"
        tid="current_marketing_channels"
        options={["Social Media / Organic", "Email marketing", "Paid Ads", "Other", "None"]}
        value={state.current_marketing_channels}
        onChange={(v) => set("current_marketing_channels", v)}
      />
      <Radio label="Have Existing Ad Accounts We Can Plug Into?" tid="has_existing_ad_accounts" options={["YES", "NO"]} value={state.has_existing_ad_accounts} onChange={(v) => set("has_existing_ad_accounts", v)} hint="Facebook Ads, Google Ads, etc. — if YES you'll share access with our tech team." />
      <Radio
        label="Monthly Ad Budget * (paid ads only)"
        tid="monthly_budget"
        options={["$1,000-$3,000", "$3,000 - $5,000", "$5,000+"]}
        value={state.monthly_budget}
        onChange={(v) => set("monthly_budget", v)}
      />
      <Radio label="Have Existing Marketing Assets?" tid="has_existing_assets" options={["YES", "NO"]} value={state.has_existing_assets} onChange={(v) => set("has_existing_assets", v)} hint="Photos, videos, testimonials, case studies…" />
      <Text label="Preferred Communication Channels For Updates?" tid="preferred_comm_channels" value={state.preferred_comm_channels} onChange={(v) => set("preferred_comm_channels", v)} placeholder="Slack, email, video call, phone…" />
      <LongText label="Anything Else We Should Know?" tid="anything_else" value={state.anything_else} onChange={(v) => set("anything_else", v)} />
    </StepShell>
  );
}

// ═════════════════════════════════════════════════════════════════════
// THANK YOU
// ═════════════════════════════════════════════════════════════════════
function ThankYou() {
  return (
    <div style={sh.page} data-testid="intake-thankyou">
      <BgFx />
      <div style={sh.welcomeWrap}>
        <div style={{ ...sh.welcomeInner, maxWidth: 640 }}>
          <img src={LOGO} alt="Skye AI" style={{ ...sh.welcomeLogo, width: 160, marginBottom: 30 }} />
          <div style={sh.doneChip}>✓ You&apos;re all set</div>
          <h1 style={{ ...sh.welcomeTitle, fontSize: 42, marginTop: 24 }}>
            Got it. Now<br />
            <span style={sh.doneAccent}>we start drafting.</span>
          </h1>
          <p style={sh.welcomeLead}>Our team just got pinged. Your kickoff call is already on the calendar — here&apos;s what happens between now and then:</p>

          {/* URGENT — Create Skye AI account before kickoff */}
          <a
            href="https://crm.skyeai.io/join/create-your-account"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="intake-thankyou-create-account"
            style={sh.urgentCard}
          >
            <div style={sh.urgentPulse} aria-hidden="true" />
            <div style={sh.urgentEyebrow}>
              <span style={sh.urgentEyebrowDot} />
              DO THIS BEFORE THE KICKOFF CALL
            </div>
            <div style={sh.urgentTitle}>Create your Skye AI account</div>
            <div style={sh.urgentSub}>
              Takes 60 seconds. Set this up now so we can walk you through your
              dashboard live on the call &mdash; without it we can&apos;t
              onboard you the same day.
            </div>
            <div style={sh.urgentBtn}>
              Create my account
              <span data-arrow="1" style={sh.urgentBtnArrow}>&rarr;</span>
            </div>
          </a>

          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 18, textAlign: "left" }}>
            {[
              ["01", "First-round drafts", "We sketch several ad directions from your intake so the kickoff call has something to react to."],
              ["02", "Kickoff call", "We sharpen offer, audience, and hooks together — you talk, we take notes."],
              ["03", "Final ads", "Sharpened creatives dropped in your inbox with a private review link."],
              ["04", "Then we go live", "Only after you approve. No surprises."],
            ].map(([n, t, d]) => (
              <div key={n} style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
                <div style={sh.doneStep}>{n}</div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 15.5, fontWeight: 700, color: "#fff", marginBottom: 3 }}>{t}</div>
                  <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={sh.doneContact}>
            Need to reach us? <a href="mailto:info@skyeai.io" style={sh.linkPink}>info@skyeai.io</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// PRIMITIVES
// ═════════════════════════════════════════════════════════════════════
function StepShell({ title, lead, children }) {
  return (
    <>
      <div style={sh.stepHeader}>
        <h1 style={sh.stepTitle}>{title}</h1>
        {lead && <p style={sh.stepLead}>{lead}</p>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>{children}</div>
    </>
  );
}

function Row({ children }) {
  return <div style={sh.gridTwo}>{children}</div>;
}

function SuggestionChip({ suggestion, onApply }) {
  if (!suggestion) return null;
  return (
    <button
      type="button"
      onClick={() => onApply(suggestion)}
      data-testid="intake-suggestion-chip"
      style={sh.suggestChip}
    >
      <span style={sh.suggestBadge}>SUGGEST</span>
      <span style={sh.suggestText}>{suggestion.length > 220 ? suggestion.slice(0, 220) + "…" : suggestion}</span>
      <span style={sh.suggestApply}>Use this →</span>
    </button>
  );
}

function Text({ label, value, onChange, placeholder = "", type = "text", tid, hint, suggestion, onApplySuggestion }) {
  return (
    <label style={sh.field}>
      <span style={sh.label}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`intake-${tid}`}
        style={sh.input}
      />
      {hint && <span style={sh.hint}>{hint}</span>}
      {suggestion && onApplySuggestion && !value && (
        <SuggestionChip suggestion={suggestion} onApply={onApplySuggestion} />
      )}
    </label>
  );
}

function LongText({ label, value, onChange, hint, tid, suggestion, onApplySuggestion }) {
  return (
    <label style={sh.field}>
      <span style={sh.label}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`intake-${tid}`}
        style={{ ...sh.input, minHeight: 120, resize: "vertical", lineHeight: 1.55, fontFamily: "inherit" }}
      />
      {hint && <span style={sh.hint}>{hint}</span>}
      {suggestion && onApplySuggestion && !value && (
        <SuggestionChip suggestion={suggestion} onApply={onApplySuggestion} />
      )}
    </label>
  );
}

function Radio({ label, options, value, onChange, hint, tid }) {
  return (
    <div style={sh.field}>
      <span style={sh.label}>{label}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        {options.map((opt) => {
          const on = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              data-testid={`intake-${tid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              style={{
                ...sh.optionBtn,
                borderColor: on ? "#B476FF" : "rgba(255,255,255,0.1)",
                background: on ? "rgba(180,118,255,0.08)" : "rgba(255,255,255,0.02)",
                color: on ? "#fff" : "rgba(255,255,255,0.75)",
                fontWeight: on ? 700 : 500,
                boxShadow: on ? "0 0 20px rgba(180,118,255,0.15)" : "none",
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: "50%",
                border: on ? "5px solid #B476FF" : "1.5px solid rgba(255,255,255,0.28)",
                flexShrink: 0, transition: "border 0.2s ease",
              }} />
              {opt}
            </button>
          );
        })}
      </div>
      {hint && <span style={sh.hint}>{hint}</span>}
    </div>
  );
}

function Checkboxes({ label, options, value, onChange, tid }) {
  const toggle = (opt) => {
    const arr = new Set(value || []);
    if (arr.has(opt)) arr.delete(opt); else arr.add(opt);
    onChange(Array.from(arr));
  };
  return (
    <div style={sh.field}>
      <span style={sh.label}>{label}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        {options.map((opt) => {
          const on = (value || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              data-testid={`intake-${tid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              style={{
                ...sh.optionBtn,
                borderColor: on ? "#B476FF" : "rgba(255,255,255,0.1)",
                background: on ? "rgba(180,118,255,0.08)" : "rgba(255,255,255,0.02)",
                color: on ? "#fff" : "rgba(255,255,255,0.75)",
                fontWeight: on ? 700 : 500,
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 4,
                border: on ? "none" : "1.5px solid rgba(255,255,255,0.28)",
                background: on ? "linear-gradient(135deg, #4ADEDE, #B476FF)" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#0A0A0A", fontSize: 10, fontWeight: 900,
                flexShrink: 0,
              }}>{on ? "✓" : ""}</span>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Website field — inline AI status pill
// ═════════════════════════════════════════════════════════════════════
function WebsiteField({ value, onChange, onBlur, suggestState }) {
  const status = suggestState?.status || "idle";
  const pill = status === "loading" ? { text: "📄 Reading your site…", bg: "rgba(74,222,222,0.08)", color: "#4ADEDE", border: "rgba(74,222,222,0.28)" }
    : status === "ready" ? { text: "✓ Suggestions ready — check the fields below", bg: "rgba(74,222,222,0.08)", color: "#4ADEDE", border: "rgba(74,222,222,0.28)" }
    : status === "error" ? { text: "Couldn't read this — that's OK, you can fill in manually", bg: "rgba(255,75,139,0.05)", color: "#FF8FB1", border: "rgba(255,75,139,0.25)" }
    : null;
  return (
    <label style={sh.field}>
      <span style={sh.label}>Your Website *</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="yourbusiness.com"
        data-testid="intake-website"
        style={{ ...sh.input, borderColor: status === "ready" ? "rgba(74,222,222,0.35)" : "rgba(255,255,255,0.10)" }}
      />
      {pill ? (
        <div style={{
          ...sh.hint,
          padding: "8px 12px",
          background: pill.bg,
          border: `1px solid ${pill.border}`,
          borderRadius: 8,
          color: pill.color,
          fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 6,
          marginTop: 6,
        }} data-testid="intake-website-status">
          {pill.text}
        </div>
      ) : (
        <span style={sh.hint}>Drop your website — we'll read it and pre-fill answers below.</span>
      )}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Address Autocomplete (Nominatim / OpenStreetMap — free, no key)
// ═════════════════════════════════════════════════════════════════════
function AddressAutocomplete({ label, tid, value, onChange }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = async (q) => {
    if (!q || q.length < 4) { setResults([]); return; }
    setLoading(true);
    try {
      // Photon (Komoot) — free, no key. Free geocoders don't have great US
      // house-number coverage, so we split the query: leading digits become
      // the "house number" we prepend to each street match. Result: user's
      // "12692 N Berwick Dr" produces `12692 N Berwick Dr, Charlotte, NC`.
      const raw = q.trim();
      const houseMatch = raw.match(/^(\d{1,7})\s+/);
      const houseNumber = houseMatch ? houseMatch[1] : "";
      const searchQ = houseMatch ? raw.slice(houseMatch[0].length) : raw;
      if (!searchQ) { setResults([]); setLoading(false); return; }

      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(searchQ)}&limit=20&lang=en&bbox=-125,24,-66,50`;
      const r = await fetch(url);
      const data = await r.json();
      const feats = Array.isArray(data?.features) ? data.features : [];
      const items = feats
        .filter((f) => (f?.properties?.countrycode || "").toUpperCase() === "US")
        .map((f) => {
          const p = f.properties || {};
          const street = p.street || p.name || "";
          const hn = p.housenumber || houseNumber;
          const line1 = [hn, street].filter(Boolean).join(" ").trim();
          const line2 = [p.city || p.town || p.village || p.county, p.state, p.postcode]
            .filter(Boolean).join(", ");
          const display = [line1, line2].filter(Boolean).join(", ");
          return {
            id: `${f.properties.osm_id || Math.random()}-${line1}`,
            line1, line2, display,
          };
        })
        .filter((r) => r.line1 && r.display);

      // Dedupe by display string, keep first 6
      const seen = new Set();
      const deduped = [];
      for (const it of items) {
        if (seen.has(it.display)) continue;
        seen.add(it.display);
        deduped.push(it);
        if (deduped.length >= 6) break;
      }
      setResults(deduped);
      setOpen(true);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  const onType = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 350);
  };

  const pick = (item) => {
    onChange(item.display);
    setResults([]);
    setOpen(false);
  };

  return (
    <label ref={wrap} style={{ ...sh.field, position: "relative", zIndex: open ? 100 : 1, isolation: "isolate" }}>
      <span style={sh.label}>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onType(e.target.value)}
        onFocus={() => value.length >= 4 && results.length && setOpen(true)}
        placeholder="123 Main St, City, State"
        data-testid={`intake-${tid}`}
        autoComplete="off"
        name={`skye-addr-${tid}`}
        style={sh.input}
      />
      {open && results.length > 0 && (
        <div style={sh.addressDropdown} data-testid="intake-address-dropdown">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => pick(r)}
              data-testid="intake-address-option"
              style={sh.addressOption}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.35 }}>{r.line1}</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", lineHeight: 1.35 }}>{r.line2}</span>
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <span style={{ ...sh.hint, color: "rgba(74,222,222,0.7)" }} data-testid="intake-address-loading">
          Looking up addresses…
        </span>
      ) : (
        <span style={sh.hint}>Include your city + state for the best match.</span>
      )}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════════════
// PDF Upload (max 20MB, drag & drop or click)
// ═════════════════════════════════════════════════════════════════════
function PdfUpload({ label, tid, file, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (f) => {
    setErr("");
    if (!f) return;
    if (f.type !== "application/pdf") { setErr("Only PDF files are accepted."); return; }
    if (f.size > 20 * 1024 * 1024) { setErr("File exceeds 20 MB limit."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/ads/upload-script`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed. Try again.");
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  return (
    <div style={sh.field}>
      <span style={sh.label}>{label}</span>
      {file?.path ? (
        <div style={sh.filePill} data-testid={`intake-${tid}-uploaded`}>
          <span style={{ fontSize: 20 }}>📄</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {file.original_filename || "script.pdf"}
            </div>
            <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.5)" }}>
              {formatBytes(file.size)} · uploaded
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`intake-${tid}-remove`}
            style={sh.fileRemove}
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          data-testid={`intake-${tid}-drop`}
          style={sh.dropZone}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: "none" }}
            data-testid={`intake-${tid}-input`}
          />
          {uploading ? (
            <span style={{ color: "#4ADEDE", fontWeight: 700 }}>Uploading…</span>
          ) : (
            <>
              <span style={{ fontSize: 30, opacity: 0.6 }}>📄</span>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 8 }}>
                Drop a PDF here or click to browse
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
                Max 20 MB · PDF only
              </div>
            </>
          )}
        </div>
      )}
      {err && (
        <div style={{ ...sh.error, marginTop: 8, marginBottom: 0 }} data-testid={`intake-${tid}-error`}>
          {err}
        </div>
      )}
    </div>
  );
}

function formatBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / 1024 / 1024).toFixed(1) + " MB";
}

// Subtle celebratory burst that matches the brand palette.
function fireConfetti(kind = "half") {
  const brand = ["#4ADEDE", "#B476FF", "#FF4B8B", "#F472B6", "#FFFFFF"];
  if (kind === "full") {
    // 100% — two-corner cannon so it feels like a proper milestone
    const shoot = (originX) => confetti({
      particleCount: 55, angle: originX < 0.5 ? 60 : 120, spread: 65,
      startVelocity: 40, gravity: 0.9, ticks: 180, decay: 0.92,
      origin: { x: originX, y: 0.85 }, colors: brand, scalar: 0.85,
    });
    shoot(0.15); shoot(0.85);
    setTimeout(() => { shoot(0.2); shoot(0.8); }, 220);
  } else {
    // 50% — gentler cannon from below-center so it doesn't overwhelm
    confetti({
      particleCount: 55, angle: 90, spread: 70,
      startVelocity: 35, gravity: 0.9, ticks: 160, decay: 0.92,
      origin: { x: 0.5, y: 0.92 }, colors: brand, scalar: 0.8,
    });
  }
}

// ═════════════════════════════════════════════════════════════════════
function BgFx() {
  return (
    <>
      <div style={sh.bgOrb1} />
      <div style={sh.bgOrb2} />
      <div style={sh.bgGrain} />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// STYLES
// ═════════════════════════════════════════════════════════════════════
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const SANS = "'Poppins', system-ui, -apple-system, sans-serif";
const GRAD = "linear-gradient(135deg, #4ADEDE 0%, #B476FF 50%, #FF4B8B 100%)";

const sh = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(1200px 700px at 15% -10%, rgba(74,222,222,0.08), transparent 60%), radial-gradient(900px 600px at 90% 10%, rgba(180,118,255,0.12), transparent 55%), #08080C",
    color: "#fff",
    fontFamily: SANS,
    position: "relative",
    overflowX: "hidden",
  },
  bgOrb1: { position: "fixed", top: "-15%", left: "-10%", width: 500, height: 500,
    background: "radial-gradient(circle, rgba(74,222,222,0.14), transparent 60%)",
    filter: "blur(60px)", pointerEvents: "none", zIndex: 0 },
  bgOrb2: { position: "fixed", bottom: "-20%", right: "-10%", width: 620, height: 620,
    background: "radial-gradient(circle, rgba(255,75,139,0.10), transparent 60%)",
    filter: "blur(80px)", pointerEvents: "none", zIndex: 0 },
  bgGrain: { position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1,
    opacity: 0.03, mixBlendMode: "overlay",
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>")` },

  welcomeWrap: { position: "relative", zIndex: 2, minHeight: "100vh",
    display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px" },
  welcomeInner: { maxWidth: 760, textAlign: "center", animation: "fade-up 0.7s ease",
    display: "flex", flexDirection: "column", alignItems: "center", margin: "0 auto" },
  welcomeLogo: { width: 260, maxWidth: "70%", height: "auto",
    marginLeft: "auto", marginRight: "auto", marginBottom: 32, display: "block",
    filter: "drop-shadow(0 8px 40px rgba(180,118,255,0.25))" },
  welcomeTitle: { fontSize: "clamp(32px, 4.5vw, 46px)", fontWeight: 800,
    lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 20, color: "#fff" },
  welcomeLead: { fontSize: "clamp(15px, 1.6vw, 18px)", lineHeight: 1.6,
    color: "rgba(255,255,255,0.65)", maxWidth: 640, margin: "0 auto 40px" },
  welcomeBtn: { padding: "18px 44px", borderRadius: 999, background: GRAD, color: "#0A0A0A",
    fontSize: 17, fontWeight: 900, letterSpacing: "-0.005em",
    border: "none", cursor: "pointer", fontFamily: SANS,
    boxShadow: "0 10px 40px rgba(180,118,255,0.35), 0 0 60px rgba(74,222,222,0.15)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease" },
  welcomeFoot: { marginTop: 28, fontSize: 12, color: "rgba(255,255,255,0.4)",
    fontFamily: MONO, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 },

  stageWrap: { position: "relative", zIndex: 2, padding: "36px 32px 20px",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
  stageRow: { display: "flex", alignItems: "center", gap: 0,
    maxWidth: 640, width: "100%", justifyContent: "center" },
  stageItem: { display: "flex", alignItems: "center", gap: 10, flex: "0 1 auto" },
  stageDot: { width: 22, height: 22, borderRadius: "50%", border: "2px solid",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.3s ease", flexShrink: 0 },
  stageLabel: { fontSize: 13.5, fontFamily: SANS,
    letterSpacing: "-0.005em", transition: "color 0.3s ease" },
  stageLine: { width: 60, height: 2, marginLeft: 14, marginRight: 4,
    borderRadius: 2, transition: "background 0.4s ease" },
  stageMeta: { marginTop: 8, fontFamily: MONO, fontSize: 11,
    letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", fontWeight: 700 },

  // Completion progress bar
  completionWrap: {
    position: "relative", zIndex: 2,
    maxWidth: 780, margin: "0 auto",
    padding: "0 24px 8px",
  },
  completionRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 8, gap: 12,
  },
  completionCheer: {
    fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.85)",
    letterSpacing: "-0.005em",
    fontFamily: SANS,
  },
  completionCount: {
    fontFamily: MONO, fontSize: 10.5, letterSpacing: 1.2,
    textTransform: "uppercase", fontWeight: 700,
    color: "rgba(74,222,222,0.75)",
    whiteSpace: "nowrap",
  },
  completionTrack: {
    height: 4, borderRadius: 999,
    background: "rgba(255,255,255,0.05)",
    overflow: "hidden",
  },
  completionFill: {
    height: "100%",
    background: "linear-gradient(90deg, #4ADEDE 0%, #B476FF 50%, #FF4B8B 100%)",
    borderRadius: 999,
    transition: "width 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    boxShadow: "0 0 12px rgba(180,118,255,0.6)",
  },

  // Sections dropdown (public users)
  secDropWrap: {
    position: "relative", zIndex: 5,
    maxWidth: 780, margin: "0 auto",
    padding: "12px 24px 8px",
    display: "flex", justifyContent: "flex-end",
  },
  secDropBtn: {
    padding: "7px 14px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 999,
    color: "rgba(255,255,255,0.7)",
    fontSize: 12, fontWeight: 700, letterSpacing: 0.2,
    cursor: "pointer", fontFamily: SANS,
    transition: "all 0.15s ease",
  },
  secDropList: {
    position: "absolute", top: "calc(100% - 4px)", right: 24,
    background: "#12121A",
    border: "1px solid rgba(180,118,255,0.28)",
    borderRadius: 12, padding: 6,
    boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
    minWidth: 220,
  },
  secDropItem: {
    display: "flex", alignItems: "center", gap: 10,
    width: "100%", padding: "9px 12px",
    background: "transparent", border: "none",
    borderRadius: 8,
    color: "#fff", cursor: "pointer",
    fontSize: 13, fontWeight: 600, fontFamily: SANS,
    textAlign: "left",
    transition: "background 0.14s ease",
  },
  secDropNum: {
    width: 20, height: 20, borderRadius: "50%",
    fontSize: 10.5, fontWeight: 900,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontFamily: MONO,
    flexShrink: 0,
  },

  // Steps navigator — 8 clickable pills
  stepsNav: {
    position: "relative", zIndex: 2,
    padding: "0 24px 24px",
    maxWidth: 1080, margin: "0 auto",
    display: "flex", gap: 8, flexWrap: "wrap",
    justifyContent: "center",
  },
  stepsNavItem: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "7px 12px",
    border: "1px solid",
    borderRadius: 999,
    fontFamily: SANS, fontSize: 12, fontWeight: 600,
    letterSpacing: "-0.005em",
    transition: "all 0.18s ease",
  },
  stepsNavNum: {
    width: 20, height: 20, borderRadius: "50%",
    fontSize: 10.5, fontWeight: 900,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontFamily: MONO,
    flexShrink: 0,
  },
  stepsNavLabel: { whiteSpace: "nowrap" },

  // Admin edit-mode banner
  adminBanner: {
    position: "sticky", top: 0, zIndex: 10,
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 32px",
    background: "linear-gradient(90deg, rgba(220,50,140,0.15), rgba(180,118,255,0.15))",
    borderBottom: "1px solid rgba(180,118,255,0.35)",
    backdropFilter: "blur(10px)",
    fontFamily: SANS,
  },
  adminBadge: {
    padding: "3px 10px", borderRadius: 999,
    background: GRAD, color: "#0A0A0A",
    fontSize: 10, fontWeight: 900, letterSpacing: 1.4,
    fontFamily: MONO,
  },
  adminMeta: { flex: 1, fontSize: 12.5, color: "rgba(255,255,255,0.8)" },
  adminBack: {
    padding: "6px 14px", borderRadius: 999,
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff", textDecoration: "none",
    fontSize: 11.5, fontWeight: 700,
  },

  // USPs alternatives block
  uspsAlt: {
    marginTop: -8,
    padding: "14px 16px",
    background: "linear-gradient(135deg, rgba(74,222,222,0.04), rgba(180,118,255,0.04))",
    border: "1px dashed rgba(180,118,255,0.28)",
    borderRadius: 12,
  },
  uspsAltHead: { display: "flex", alignItems: "center", gap: 10 },
  uspsAltTitle: {
    fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.85)",
  },
  uspsAltBtn: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "10px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10, cursor: "pointer",
    fontFamily: SANS,
    transition: "all 0.18s ease",
  },

  content: { position: "relative", zIndex: 2, maxWidth: 780, margin: "0 auto",
    padding: "0 24px 80px" },
  cardWrap: { animation: "fade-up 0.5s ease" },
  gradBorder: { borderRadius: 20, padding: 1.5, background: GRAD,
    boxShadow: "0 20px 60px rgba(74,222,222,0.08), 0 0 60px rgba(180,118,255,0.06)" },
  gradInner: { background: "linear-gradient(180deg, rgba(20,20,28,0.95) 0%, rgba(12,12,20,0.98) 100%)",
    borderRadius: 18.5, padding: "44px 42px", backdropFilter: "blur(10px)" },

  stepHeader: { textAlign: "center", marginBottom: 36, paddingBottom: 24,
    borderBottom: "1px solid rgba(255,255,255,0.06)" },
  stepTitle: { fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800,
    letterSpacing: "-0.02em", marginBottom: 10, color: "#fff", lineHeight: 1.15 },
  stepLead: { fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,0.55)",
    maxWidth: 540, margin: "0 auto" },

  gridTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 },
  field: { display: "flex", flexDirection: "column", gap: 10 },
  label: { fontSize: 13.5, fontWeight: 700, color: "rgba(255,255,255,0.9)",
    letterSpacing: "-0.005em" },
  hint: { fontSize: 12.5, color: "rgba(255,255,255,0.45)", lineHeight: 1.5, marginTop: 4 },
  input: { padding: "13px 16px", background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10,
    fontSize: 14.5, color: "#fff", fontFamily: SANS, outline: "none",
    transition: "border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease" },
  optionBtn: { display: "flex", alignItems: "center", gap: 12,
    padding: "13px 16px", border: "1px solid", borderRadius: 10,
    fontSize: 14, textAlign: "left", cursor: "pointer",
    fontFamily: SANS, transition: "all 0.18s ease" },

  actions: { marginTop: 36, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 24 },
  actionRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  btnPrimary: { padding: "13px 32px", borderRadius: 999, background: GRAD, color: "#0A0A0A",
    fontSize: 14, fontWeight: 800, letterSpacing: "-0.005em",
    border: "none", cursor: "pointer", fontFamily: SANS,
    boxShadow: "0 8px 24px rgba(180,118,255,0.28)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease" },
  btnGhost: { padding: "13px 22px", borderRadius: 999,
    background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.7)",
    fontSize: 13, fontWeight: 700, border: "1px solid rgba(255,255,255,0.08)",
    cursor: "pointer", fontFamily: SANS },
  error: { padding: "12px 16px", background: "rgba(255,75,139,0.08)",
    border: "1px solid rgba(255,75,139,0.35)", color: "#FF8FB1",
    borderRadius: 10, fontSize: 13.5, marginBottom: 14, fontFamily: SANS },

  // Suggestion chip under a field
  suggestChip: {
    marginTop: 6,
    padding: "10px 12px",
    display: "flex", alignItems: "flex-start", gap: 10,
    background: "linear-gradient(135deg, rgba(74,222,222,0.06), rgba(180,118,255,0.06))",
    border: "1px solid rgba(180,118,255,0.28)",
    borderRadius: 10, cursor: "pointer",
    fontFamily: SANS, textAlign: "left",
    transition: "all 0.18s ease",
  },
  suggestBadge: {
    padding: "2px 7px", borderRadius: 999,
    fontSize: 9.5, fontWeight: 900, letterSpacing: 0.5,
    background: GRAD, color: "#0A0A0A", flexShrink: 0,
    fontFamily: SANS, marginTop: 1,
  },
  suggestText: {
    flex: 1, fontSize: 12.5, lineHeight: 1.5,
    color: "rgba(255,255,255,0.85)",
  },
  suggestApply: {
    fontSize: 11.5, fontWeight: 700, color: "#4ADEDE", flexShrink: 0,
    marginTop: 1, whiteSpace: "nowrap",
  },

  // Address autocomplete
  addressDropdown: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
    background: "#0F0F16",
    border: "1px solid rgba(180,118,255,0.4)",
    borderRadius: 10, overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(0,0,0,0.4)",
    zIndex: 999, maxHeight: 300, overflowY: "auto",
    fontFamily: SANS,
  },
  addressOption: {
    width: "100%", textAlign: "left",
    padding: "12px 14px",
    background: "#0F0F16", border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    color: "#fff", cursor: "pointer",
    display: "flex", flexDirection: "column", gap: 2,
    fontFamily: SANS,
    fontSize: 14,
    lineHeight: 1.4,
    transition: "background 0.14s ease",
  },

  // PDF upload
  dropZone: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: "28px 20px",
    background: "rgba(255,255,255,0.02)",
    border: "1.5px dashed rgba(255,255,255,0.16)",
    borderRadius: 12, cursor: "pointer",
    transition: "all 0.18s ease",
  },
  filePill: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px",
    background: "rgba(74,222,222,0.05)",
    border: "1px solid rgba(74,222,222,0.28)",
    borderRadius: 10,
  },
  fileRemove: {
    padding: "6px 12px", borderRadius: 999,
    background: "rgba(255,75,139,0.08)", border: "1px solid rgba(255,75,139,0.28)",
    color: "#FF8FB1", cursor: "pointer",
    fontSize: 11.5, fontWeight: 700, fontFamily: SANS,
  },

  doneChip: { display: "inline-block", fontFamily: MONO, fontSize: 11.5,
    letterSpacing: 1.6, textTransform: "uppercase", color: "#4ADEDE",
    fontWeight: 800, padding: "6px 14px",
    background: "rgba(74,222,222,0.08)", border: "1px solid rgba(74,222,222,0.28)",
    borderRadius: 999, marginBottom: 8 },
  doneAccent: { background: GRAD, WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent", backgroundClip: "text", fontStyle: "italic" },
  doneStep: { fontFamily: MONO, fontSize: 12, fontWeight: 800,
    color: "#4ADEDE", letterSpacing: 1.4, padding: "6px 10px",
    background: "rgba(74,222,222,0.06)", border: "1px solid rgba(74,222,222,0.2)",
    borderRadius: 8, flexShrink: 0 },
  doneContact: { marginTop: 44, padding: "14px 20px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12,
    fontFamily: MONO, fontSize: 12.5,
    color: "rgba(255,255,255,0.55)", letterSpacing: 0.3 },
  linkPink: { color: "#FF4B8B", textDecoration: "none", fontWeight: 800 },

  // URGENT — create account CTA on Thank You page
  urgentCard: {
    position: "relative",
    display: "block",
    textDecoration: "none",
    margin: "12px auto 0",
    maxWidth: 560,
    padding: "26px 28px 30px",
    borderRadius: 20,
    background: "linear-gradient(135deg, rgba(220,50,140,0.16) 0%, rgba(180,118,255,0.14) 50%, rgba(74,222,222,0.10) 100%)",
    border: "1.5px solid rgba(220,50,140,0.55)",
    boxShadow: "0 0 0 1px rgba(220,50,140,0.15), 0 20px 60px -20px rgba(220,50,140,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
    textAlign: "left",
  },
  urgentPulse: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, transparent 0%, #FF4B8B 30%, #B476FF 50%, #4ADEDE 70%, transparent 100%)",
    backgroundSize: "200% 100%",
    animation: "urgentSlide 2.4s linear infinite",
  },
  urgentEyebrow: {
    display: "inline-flex", alignItems: "center", gap: 8,
    fontFamily: MONO, fontSize: 11, fontWeight: 800,
    letterSpacing: 1.5, textTransform: "uppercase",
    color: "#FF6BA5",
    marginBottom: 12,
  },
  urgentEyebrowDot: {
    width: 8, height: 8, borderRadius: "50%",
    background: "#FF4B8B",
    boxShadow: "0 0 0 4px rgba(255,75,139,0.18)",
    animation: "urgentPulseDot 1.4s ease-in-out infinite",
    flexShrink: 0,
  },
  urgentTitle: {
    fontFamily: SANS, fontSize: 22, fontWeight: 800,
    letterSpacing: "-0.015em", lineHeight: 1.2,
    color: "#fff",
    marginBottom: 10,
  },
  urgentSub: {
    fontFamily: SANS, fontSize: 14.5, lineHeight: 1.55,
    color: "rgba(255,255,255,0.72)",
    marginBottom: 20,
  },
  urgentBtn: {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: "13px 22px",
    borderRadius: 999,
    background: GRAD,
    color: "#0A0A0A",
    fontFamily: SANS, fontSize: 14.5, fontWeight: 900,
    letterSpacing: "-0.005em",
    boxShadow: "0 8px 24px rgba(220,50,140,0.35)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  urgentBtnArrow: {
    fontSize: 18, lineHeight: 1, transition: "transform 0.2s ease",
  },
};

function labelFor(key) {
  return ({
    first_name: "your first name",
    last_name: "your last name",
    business_name: "your business name",
    business_address: "your business address",
    business_phone: "your business phone",
    business_email: "your business email",
    website: "your website",
    project_lead: "the project lead",
    qualifying_questions: "qualifying questions",
    faqs_objections: "FAQs / objection handling",
    appointment_location: "appointment location",
  })[key] || key;
}

if (typeof document !== "undefined" && !document.getElementById("intake-form-anim")) {
  const st = document.createElement("style");
  st.id = "intake-form-anim";
  st.textContent = `
    @keyframes fade-up {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes urgentSlide {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes urgentPulseDot {
      0%, 100% { box-shadow: 0 0 0 4px rgba(255,75,139,0.18); }
      50%      { box-shadow: 0 0 0 8px rgba(255,75,139,0.05); }
    }
    a[data-testid="intake-thankyou-create-account"]:hover {
      transform: translateY(-3px);
      border-color: rgba(220,50,140,0.85) !important;
      box-shadow: 0 0 0 1px rgba(220,50,140,0.25),
                  0 30px 80px -20px rgba(220,50,140,0.6),
                  inset 0 1px 0 rgba(255,255,255,0.08) !important;
    }
    a[data-testid="intake-thankyou-create-account"]:hover span[data-arrow] { transform: translateX(4px); }
    input[data-testid^="intake-"]:focus,
    textarea[data-testid^="intake-"]:focus {
      border-color: rgba(180,118,255,0.6) !important;
      background: rgba(255,255,255,0.05) !important;
      box-shadow: 0 0 0 3px rgba(180,118,255,0.12);
    }
    input[data-testid^="intake-"]::placeholder,
    textarea[data-testid^="intake-"]::placeholder {
      color: rgba(255,255,255,0.28);
    }
    button[data-testid="intake-start"]:hover,
    button[data-testid="intake-next"]:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 14px 44px rgba(180,118,255,0.4), 0 0 60px rgba(74,222,222,0.2) !important;
    }
    button[data-testid="intake-back"]:hover {
      background: rgba(255,255,255,0.08) !important;
      color: #fff !important;
    }
    button[data-testid="intake-suggestion-chip"]:hover {
      border-color: rgba(180,118,255,0.55) !important;
      background: linear-gradient(135deg, rgba(74,222,222,0.10), rgba(180,118,255,0.10)) !important;
      transform: translateY(-1px);
    }
    button[data-testid^="intake-usps-alt-"]:hover {
      background: rgba(180,118,255,0.06) !important;
      border-color: rgba(180,118,255,0.35) !important;
    }
    button[data-testid^="intake-step-nav-"]:not(:disabled):hover {
      transform: translateY(-1px);
      border-color: rgba(180,118,255,0.4) !important;
    }
    button[data-testid="intake-sections-toggle"]:hover {
      background: rgba(255,255,255,0.06) !important;
      color: #fff !important;
      border-color: rgba(180,118,255,0.35) !important;
    }
    button[data-testid^="intake-section-item-"]:not(:disabled):hover {
      background: rgba(180,118,255,0.10) !important;
    }
    [data-testid="intake-address-option"]:hover {
      background: rgba(180,118,255,0.10) !important;
    }
    /* Field entrance animation — staggered fade-up inside each step */
    [data-testid^="intake-"] label, [data-testid="intake-form"] .step-field {
      animation: field-in 0.4s ease both;
    }
    @keyframes field-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    /* Mobile — collapse 2-col rows, roomier tap targets, sticky Continue */
    @media (max-width: 640px) {
      [data-testid="intake-form"] { font-size: 14.5px; }
      div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
      div[style*="grid-template-columns: 1fr 1fr"] > * { min-width: 0; }
      input[data-testid^="intake-"],
      textarea[data-testid^="intake-"] {
        font-size: 16px !important;  /* prevents iOS auto-zoom */
        padding: 14px 14px !important;
      }
      button[data-testid="intake-start"] { padding: 20px 36px !important; font-size: 16px !important; }
      button[data-testid="intake-next"],
      button[data-testid="intake-back"] {
        padding: 15px 24px !important;
        font-size: 14px !important;
      }
      button[data-testid="intake-next"] { flex: 1; }
      /* Roomier card padding on mobile */
      div[style*="borderRadius: 18.5"] { padding: 28px 20px !important; }
      /* Sections dropdown wraps neatly */
      [data-testid="intake-sections-list"] { right: 12px !important; left: 12px !important; min-width: 0 !important; }
      /* Welcome hero: smaller logo + reduced padding */
      img[data-testid="intake-welcome-logo"] { width: 200px !important; margin-bottom: 20px !important; }
    }
    @media (max-width: 640px) {
      .grid-two { grid-template-columns: 1fr !important; }
    }
  `;
  document.head.appendChild(st);
}
